import { createApiUrl } from "../config/api";
import type { Store } from "@reduxjs/toolkit";

type ResponseType = "json" | "text" | "blob" | "void" | "raw";

export class ApiError extends Error {
  status: number;
  data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export type RefreshAction = (
  reason: "fetch" | "verify" | "scheduled" | "visibility" | "manual" | null
) => any;

type ConfigureOptions = {
  refresh: RefreshAction;
  logout: () => any;
};

type RequestOptions = RequestInit & {
  responseType?: ResponseType;
};

/**
 * Единый HTTP-клиент с поддержкой:
 * - префикса BASE_URL
 * - автоматического проброса accessToken
 * - единственного запроса на refresh токена
 * - унифицированного разбора ответа
 */
class HttpClient {
  private store: Store | null = null;
  private refreshAction: RefreshAction | null = null;
  private logoutAction: (() => any) | null = null;
  private refreshPromise: Promise<any> | null = null;

  configure(store: Store, actions: ConfigureOptions) {
    this.store = store;
    this.refreshAction = actions.refresh;
    this.logoutAction = actions.logout;
  }

  /**
   * Базовый fetch c автоматическим добавлением токена и единоразовым refresh на 401.
   * Не бросает ошибку по статусам, чтобы сохранить семантику обычного fetch.
   */
  async fetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
    const url = typeof input === "string" ? createApiUrl(input) : input;
    const request = () => this.performFetch(url, init);

    let response = await request();
    if (response.status !== 401) return response;

    const refreshed = await this.tryRefreshToken();
    if (!refreshed) return response;

    // Retry with updated token
    response = await request();
    return response;
  }

  /**
   * Высокоуровневый запрос: бросает ApiError на !ok и парсит тело согласно responseType.
   */
  async request<T = any>(input: RequestInfo | URL, options: RequestOptions = {}): Promise<T> {
    const { responseType = "json", ...init } = options;
    const response = await this.fetch(input, init);

    if (!response.ok) {
      throw await this.buildApiError(response);
    }

    return this.parseResponse<T>(response, responseType);
  }

  private async performFetch(url: RequestInfo | URL, init: RequestInit): Promise<Response> {
    const headers = this.buildHeaders(init.headers, init.body);

    return fetch(url, {
      ...init,
      headers,
    });
  }

  private buildHeaders(headersInit?: HeadersInit, body?: any): Headers {
    const headers = new Headers(headersInit || {});
    const token = this.getAccessToken();

    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const isJsonBody =
      body &&
      !(body instanceof FormData) &&
      typeof body === "string" &&
      !headers.has("Content-Type");

    if (isJsonBody) {
      headers.set("Content-Type", "application/json");
    }

    return headers;
  }

  private getAccessToken(): string | null {
    try {
      return localStorage.getItem("accessToken");
    } catch {
      return null;
    }
  }

  private async tryRefreshToken(): Promise<boolean> {
    if (!this.store || !this.refreshAction) return false;

    if (!this.refreshPromise) {
      const action = this.refreshAction("fetch");
      const dispatched = this.store.dispatch(action) as any;
      this.refreshPromise =
        typeof dispatched?.unwrap === "function"
          ? dispatched.unwrap()
          : Promise.resolve(dispatched);
    }

    try {
      await this.refreshPromise;
      return true;
    } catch (err) {
      if (this.logoutAction && this.store) {
        this.store.dispatch(this.logoutAction());
      }
      return false;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async parseResponse<T>(response: Response, type: ResponseType): Promise<T> {
    switch (type) {
      case "raw":
        return response as unknown as T;
      case "blob":
        return (await response.blob()) as unknown as T;
      case "text":
        return (await response.text()) as unknown as T;
      case "void":
        return undefined as unknown as T;
      case "json":
      default:
        return (await response.json()) as T;
    }
  }

  private async buildApiError(response: Response): Promise<ApiError> {
    let message = `Request failed with status ${response.status}`;
    let data: any = null;

    try {
      data = await response.clone().json();
      if (data?.message) {
        message = data.message;
      } else if (typeof data?.error === "string") {
        message = data.error;
      }
    } catch {
      try {
        const text = await response.text();
        if (text) {
          message = text;
        }
      } catch {}
    }

    return new ApiError(message, response.status, data);
  }
}

export const httpClient = new HttpClient();

export const initializeApiClient = (
  store: Store,
  actions: ConfigureOptions
) => {
  httpClient.configure(store, actions);
};
